import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext();

const translations = {
  en: {
    nav: {
      home: 'Home',
      gallery: 'Gallery',
      about: 'About',
      exhibitions: 'Exhibitions',
      contact: 'Contact',
      admin: 'Admin'
    },
    hero: {
      title: 'ALEXI DELFIEUX',
      subtitle: 'aka MOZER',
      tagline: 'Paintings from Paris suburbs to open horizons',
      location: 'Based between France & Australia',
      cta: 'Explore Gallery'
    },
    gallery: {
      title: 'Gallery',
      allWorks: 'All Works',
      filters: 'Filter by Series',
      available: 'Available',
      sold: 'Sold',
      inquire: 'Inquire',
      viewDetails: 'View Details',
      series: {
        all: 'All Works',
        saintMartin: 'Saint Martin & Cityscapes',
        rise: 'Rise in Saint Martin',
        bloc: 'Bloc & Structures',
        earlyMozer: 'Early MOZER Era',
        origins: 'Origins & Totems'
      },
      details: {
        size: 'Size',
        medium: 'Medium',
        colors: 'Main Colors',
        price: 'Price',
        year: 'Year',
        description: 'Description'
      }
    },
    about: {
      title: 'About the Artist',
      statement: 'Artist Statement',
      process: 'Process & Materials',
      bio: 'Biography',
      education: 'Education',
      statementText: "My paintings come from two opposite directions: the dense geometry of the Paris suburbs where I grew up, and the open horizons I have been travelling through in the last few years. I first worked as a graphic designer and in digital marketing, producing images for clients and brands. At some point the briefs felt repetitive and I realised I needed to reclaim creativity for myself. Painting became that space.",
      statementText2: "In Australia the red earth makes the sky feel wider, bluer, with whiter clouds. Travelling through around twenty countries has sharpened my senses: you notice details locals no longer see - the light on a wall, a colour combination, the way people eat, talk or play music.",
      statementText3: "I try to keep that traveller's gaze even when I return to places I know very well. On the canvas I combine blocks, lines and taped shapes that come from suburbs and architecture with more fluid gestures, sprays and drips that recall graffiti, skies and weather. Each painting becomes a fragment of a map where rigid structures and human complexity meet, and where simple gestures like sharing or caring still connect us beneath the layers.",
      processText: "I use spray cans, Posca markers, rollers, and acrylic paint. To build my shapes I use a lot of masking tape: it allows me to draw sharp lines, cut blocks and bring the canvas closer to the architecture of the suburbs I know. I like to work outdoors. The wind moves the spray, the splashes and drips are not fully predictable, and the sun helps the paint dry faster.",
      processText2: "When I lay thick layers of acrylic, sometimes combined with chrome paint, cracks appear as it dries and create an earthy, eroded surface. What interests me is the balance between control and accident: the areas built with tape and rollers, very graphic, and the areas left to gesture, wind and time. The painting becomes a ground where I build a structure but also let the material live its own life.",
      bioText: "Alexi Delfieux is a French painter based between France and Australia. He first worked as a graphic designer and in digital marketing before leaving Paris to travel and take on manual jobs across Europe, Australia and Southeast Asia.",
      seeking: "Currently seeking exhibitions, residencies and patronage to dedicate more time to painting."
    },
    exhibitions: {
      title: 'Exhibitions',
      upcoming: 'Upcoming',
      past: 'Past Exhibitions',
      collaboration: 'Open to Collaborations',
      residencies: 'Open to Residencies'
    },
    contact: {
      title: 'Contact',
      subtitle: 'Get in Touch',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      message: 'Message',
      send: 'Send Message',
      sending: 'Sending...',
      success: 'Message sent successfully!',
      error: 'Failed to send message. Please try again.',
      commission: 'Commission Work',
      commissionText: 'Interested in commissioning a custom artwork? I create unique pieces tailored to your vision, combining urban architecture with fluid abstraction. Contact me to discuss your project, preferred dimensions, color palette, and timeline. Each commission is a collaborative journey.',
      info: 'Contact Information',
      basedIn: 'Based in'
    },
    admin: {
      title: 'Admin Panel',
      login: 'Login',
      logout: 'Logout',
      username: 'Username',
      password: 'Password',
      artworks: 'Manage Artworks',
      emailSettings: 'Email Settings',
      availability: 'Update Availability',
      save: 'Save Changes',
      saved: 'Changes saved successfully!',
      emailService: 'Email Service',
      apiKey: 'API Key',
      fromEmail: 'From Email',
      toEmail: 'Recipient Email'
    },
    footer: {
      rights: 'All rights reserved.',
      follow: 'Follow me'
    }
  },
  fr: {
    nav: {
      home: 'Accueil',
      gallery: 'Galerie',
      about: 'À Propos',
      exhibitions: 'Expositions',
      contact: 'Contact',
      admin: 'Admin'
    },
    hero: {
      title: 'ALEXI DELFIEUX',
      subtitle: 'alias MOZER',
      tagline: 'Peintures de la banlieue parisienne aux horizons ouverts',
      location: 'Basé entre la France et l\'Australie',
      cta: 'Explorer la Galerie'
    },
    gallery: {
      title: 'Galerie',
      allWorks: 'Toutes les Œuvres',
      filters: 'Filtrer par Série',
      available: 'Disponible',
      sold: 'Vendu',
      inquire: 'Demander',
      viewDetails: 'Voir Détails',
      series: {
        all: 'Toutes les Œuvres',
        saintMartin: 'Saint Martin & Paysages Urbains',
        rise: 'Rise in Saint Martin',
        bloc: 'Bloc & Structures',
        earlyMozer: 'Ère MOZER Précoce',
        origins: 'Origines & Totems'
      },
      details: {
        size: 'Dimensions',
        medium: 'Technique',
        colors: 'Couleurs Principales',
        price: 'Prix',
        year: 'Année',
        description: 'Description'
      }
    },
    about: {
      title: 'À Propos de l\'Artiste',
      statement: 'Démarche Artistique',
      process: 'Processus & Matériaux',
      bio: 'Biographie',
      education: 'Formation',
      statementText: "Mes peintures viennent de deux directions opposées : la géométrie dense de la banlieue parisienne où j'ai grandi, et les horizons ouverts que j'ai traversés ces dernières années. J'ai d'abord travaillé comme graphiste et dans le marketing digital, produisant des images pour des clients et des marques. À un moment donné, les briefs sont devenus répétitifs et j'ai réalisé que je devais récupérer la créativité pour moi-même. La peinture est devenue cet espace.",
      statementText2: "En Australie, la terre rouge rend le ciel plus large, plus bleu, avec des nuages plus blancs. Voyager à travers une vingtaine de pays a aiguisé mes sens : vous remarquez des détails que les locaux ne voient plus - la lumière sur un mur, une combinaison de couleurs, la façon dont les gens mangent, parlent ou jouent de la musique.",
      statementText3: "J'essaie de garder ce regard de voyageur même quand je retourne dans des endroits que je connais très bien. Sur la toile, je combine des blocs, des lignes et des formes scotchées qui viennent de la banlieue et de l'architecture avec des gestes plus fluides, des pulvérisations et des coulures qui rappellent le graffiti, le ciel et le temps. Chaque peinture devient un fragment de carte où les structures rigides et la complexité humaine se rencontrent, et où des gestes simples comme partager ou prendre soin connectent encore sous les couches.",
      processText: "J'utilise des bombes de peinture, des marqueurs Posca, des rouleaux et de la peinture acrylique. Pour construire mes formes, j'utilise beaucoup de ruban adhésif : cela me permet de tracer des lignes nettes, de découper des blocs et de rapprocher la toile de l'architecture de la banlieue que je connais. J'aime travailler en extérieur. Le vent déplace la pulvérisation, les éclaboussures et les coulures ne sont pas entièrement prévisibles, et le soleil aide la peinture à sécher plus rapidement.",
      processText2: "Quand je pose des couches épaisses d'acrylique, parfois combinées avec de la peinture chromée, des fissures apparaissent en séchant et créent une surface terreuse et érodée. Ce qui m'intéresse, c'est l'équilibre entre contrôle et accident : les zones construites avec du ruban et des rouleaux, très graphiques, et les zones laissées au geste, au vent et au temps. La peinture devient un terrain où je construis une structure mais laisse aussi le matériau vivre sa propre vie.",
      bioText: "Alexi Delfieux est un peintre français basé entre la France et l'Australie. Il a d'abord travaillé comme graphiste et dans le marketing digital avant de quitter Paris pour voyager et accepter des emplois manuels à travers l'Europe, l'Australie et l'Asie du Sud-Est.",
      seeking: "Actuellement en recherche d'expositions, de résidences et de formes de mécénat artistique."
    },
    exhibitions: {
      title: 'Expositions',
      upcoming: 'À Venir',
      past: 'Expositions Passées',
      collaboration: 'Ouvert aux Collaborations',
      residencies: 'Ouvert aux Résidences'
    },
    contact: {
      title: 'Contact',
      subtitle: 'Prenez Contact',
      name: 'Nom',
      email: 'Email',
      phone: 'Téléphone',
      message: 'Message',
      send: 'Envoyer le Message',
      sending: 'Envoi...',
      success: 'Message envoyé avec succès !',
      error: 'Échec de l\'envoi du message. Veuillez réessayer.',
      commission: 'Travail sur Commande',
      commissionText: 'Intéressé par une œuvre sur mesure ? Je crée des pièces uniques adaptées à votre vision, combinant architecture urbaine et abstraction fluide. Contactez-moi pour discuter de votre projet, dimensions préférées, palette de couleurs et délais. Chaque commande est un voyage collaboratif.',
      info: 'Informations de Contact',
      basedIn: 'Basé à'
    },
    admin: {
      title: 'Panneau d\'Administration',
      login: 'Connexion',
      logout: 'Déconnexion',
      username: 'Nom d\'utilisateur',
      password: 'Mot de passe',
      artworks: 'Gérer les Œuvres',
      emailSettings: 'Paramètres Email',
      availability: 'Mettre à jour la Disponibilité',
      save: 'Enregistrer les Modifications',
      saved: 'Modifications enregistrées avec succès !',
      emailService: 'Service Email',
      apiKey: 'Clé API',
      fromEmail: 'Email Expéditeur',
      toEmail: 'Email Destinataire'
    },
    footer: {
      rights: 'Tous droits réservés.',
      follow: 'Suivez-moi'
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'fr' : 'en';
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
